import React from 'react';
import PageLayout from '@components/PageLayout';
import styles from './DocumentationPage.module.css';

const DocumentationPage: React.FC = () => {
  const documentationCategories = [
    {
      id: 1,
      title: 'Software & SaaS Documents',
      description: 'Deep product documentation for on-prem and cloud (SaaS) solutions.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      ),
      buttonText: 'View Documents',
      link: 'https://documentation.commvault.com/',
    },
    {
      id: 2,
      title: 'Procedures & How-To Guides',
      description: 'Step-by-step procedures (installation, configuration, backup workflows).',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
        </svg>
      ),
      buttonText: 'View Procedures',
      link: 'https://documentation.commvault.com/',
    },
    {
      id: 3,
      title: 'API & Developer Tools',
      description: 'API endpoints, example requests/responses, entity definitions.',
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6"></polyline>
          <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
      ),
      buttonText: 'View Tools',
      link: 'https://documentation.commvault.com/',
    },
  ];

  const handleActionClick = (link: string) => {
    window.open(link, '_blank');
  };

  const benefits = [
    'Authoritative source: The Documentation Center is the canonical reference for how features work, their constraints, and use cases.',
    'Support & enabling aid: It helps users, admins, and support engineers in setup, configuration, troubleshooting without needing to escalate every question.',
    'Developer enablement: API docs support automation and integration efforts.',
    'Change tracking: "What\'s New" and versioned docs help users understand new features or deprecated behavior.',
    'Consistency & trust: Having a centralized, well-maintained documentation hub enhances customer confidence and reduces conflicting or outdated information.',
  ];

  return (
    <PageLayout>
      <div className={styles.unifiedContainer}>
        {/* Documentation Info */}
        <section className={styles.documentationSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.documentationIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </span>
            Documentation
          </h2>
          <p className={styles.subtitle}>
            The fastest, cleanest, most complete data protection, security and recovery documentation
          </p>
          <ul className={styles.introList}>
            <li>
              The Documentation Center is the central hub for official, versioned product documentation for Commvault's software, services, and SaaS offerings.
            </li>
            <li>
              It includes user guides, configuration procedures, API references, release notes, and "What's New" content.
            </li>
            <li>
              The site is organized by product line / release (e.g. Software v11, SaaS) and by functionality area (e.g. agents, backup, virtualization).
            </li>
          </ul>
          <div className={styles.smallLine}></div>
        </section>

        {/* Category Cards Grid */}
        <div className={styles.categoryGrid}>
          {documentationCategories.map((category) => (
            <div key={category.id} className={styles.categoryCard}>
              <h3 className={styles.cardTitle}>{category.title}</h3>
              <div className={styles.cardHeaderLine}></div>
              <div className={styles.cardIconWrapper}>
                <div className={styles.squareIcon}>
                  <div className={styles.iconOuter}></div>
                  <div className={styles.iconInner}>
                    <div className={styles.cardIcon}>{category.icon}</div>
                  </div>
                </div>
              </div>
              <p className={styles.cardDescription}>{category.description}</p>
              <button
                className={styles.cardButton}
                onClick={() => handleActionClick(category.link)}
              >
                {category.buttonText}
              </button>
            </div>
          ))}
        </div>

        {/* Value & Benefits */}
        <section className={styles.benefitsSection}>
          <h2 className={styles.benefitsTitle}>Value & Benefits</h2>
          <ul className={styles.benefitsList}>
            {benefits.map((benefit, index) => (
              <li key={index}>
                <strong>{benefit.split(':')[0]}:</strong>{' '}
                {benefit.split(':').slice(1).join(':')}
              </li>
            ))}
          </ul>
        </section>

        {/* Launch Button */}
        <div className={styles.launchSection}>
          <button
            className={styles.launchButton}
            onClick={() => handleActionClick('https://documentation.commvault.com/')}
          >
            Launch Documentation Center
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default DocumentationPage;
